import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPinned, PenTool, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { HeroVideo, HeroVideoHandle } from "@/components/HeroVideo";

const steps = [
  { icon: MapPinned, t: "Homeowner finds their property", d: "Address search drops them onto live satellite imagery of their home." },
  { icon: PenTool, t: "They outline the surface", d: "Driveway, patio or lawn — tap the corners, exclude the house, see square footage." },
  { icon: Inbox, t: "You get a priced lead", d: "Your rates, your minimums. The lead lands in your dashboard and inbox with the measured map." },
];

export default function Landing() {
  const videoRef = useRef<HeroVideoHandle>(null);
  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" aria-label="Instant Quote Wizard home"><BrandLogo className="h-12 sm:h-16" /></Link>
        <div className="flex gap-2">
          <Button variant="ghost" asChild data-testid="landing-login-link"><Link to="/login">Sign in</Link></Button>
          <Button asChild data-testid="landing-register-link"><Link to="/register">Start pilot</Link></Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 pt-16 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="max-w-3xl fade-up min-w-0">
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">Built for Home Service Companies</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">Let homeowners measure their own job — and send you a priced lead.</h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl">A branded satellite estimating widget for your existing website. Your pricing rules, server-verified measurements, and a simple lead inbox. Nothing more.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button size="lg" asChild className="h-12 px-6" data-testid="hero-cta-register"><Link to="/register">Create your account <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button size="lg" variant="outline" className="h-12 px-6" onClick={() => videoRef.current?.playWithSound()} data-testid="hero-see-how">See how it works</Button>
            <span className="self-center text-sm text-muted-foreground">Pilot plan · $99/month</span>
          </div>
        </div>
        <div className="min-w-0 fade-up"><HeroVideo ref={videoRef} /></div>
        </div>
        <div className="mt-24 grid md:grid-cols-3 gap-px bg-border border rounded-xl overflow-hidden">
          {steps.map((s, i) => (
            <div key={s.t} className="bg-card p-8 fade-up" style={{ animationDelay: `${120 * i}ms` }}>
              <s.icon className="h-6 w-6 text-primary" />
              <h3 className="mt-6 font-bold text-lg">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-xs text-muted-foreground">Estimates shown to homeowners are preliminary and non-binding. <Link className="underline" to="/terms">Terms</Link> · <Link className="underline" to="/privacy">Privacy</Link></p>
      </main>
    </div>
  );
}
